import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { filter, distinctUntilChanged } from 'rxjs';
import { routeUrls } from '../../constants/constants';
import { UserService } from '../../services/user.service';
import { CricteamsChatService } from '../../services/cricteams-chat.service';
import { SearchPipe } from '../../pipes/search.pipe';
import { FormsModule } from '@angular/forms';
import { Club, ClubSubscriptionInfo } from '../../models/club/club-info.model';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AccountComponent } from '../account/account.component';
import { OutsideClickDirective } from '../../directives/outside-click.directive';
import { AddMatchComponent } from '../../pages/matches/add-match/add-match.component';
import { AddPracticeComponent } from '../../pages/practices/add-practice/add-practice.component';
import { AddPLayerComponent } from "../../pages/players/add-player/add-player.component";
import { AddTournamentComponent } from "../../pages/tournaments/add-tournament/add-tournament.component";
import { AddGroundComponent } from "../../pages/admin/ground/add-ground/add-ground.component";
import { AddLeaguesComponent } from "../../pages/admin/leagus/add-leagues/add-leagues.component";
import { PlayerService } from '../../services/player.service';
import { PlayerInfo } from '../../models/player/player-request.model';
import { CommonService } from '../../services/common.service';
import { ClubService } from '../../services/club.service';
import { UserBaseInfo } from '../../models/user/user-base-info.model';
import { Tournament } from '../../models/club/club-tournament.model';
import { TournamentService } from '../../services/tournament.service';
import { AnalyticsService } from '../../services/analytics.service';
import { analyticsEvents } from '../../constants/analytics';
import { AlertService } from '../../services/alert.service';
import { clearAuthStorage } from '../../utilities/auth-storage';
@Component({
  selector: 'cricteams-header',
  standalone: true,
  imports: [
    CommonModule,
    SearchPipe,
    FormsModule,
    TabsModule,
    AccountComponent,
    OutsideClickDirective,
    AddMatchComponent,
    AddPracticeComponent,
    AddPLayerComponent,
    AddTournamentComponent,
    AddGroundComponent,
    AddLeaguesComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [BsModalService],
})
export class HeaderComponent implements OnInit {
  modalRef!: BsModalRef;
  protected constants = routeUrls;
  isClubDropdownOpen = false;
  isAddDropdownOpen = false;
  clubName = '';
  fullName = '';
  displayClubDropdown = false;
  isManagerOrCaptain!: boolean;
  playerDetails!: PlayerInfo | null;
  public clubsList: Club[] = [];
  public activeClub: Club | null = null;
  public clubSearchKeyword = '';
  @Output() toggleClass = new EventEmitter<void>();
  @Output() upgradeClick = new EventEmitter<void>();
  public clubImgUri = '';
  public clubSubscription: ClubSubscriptionInfo | null = null;

  /**
   * Short label for the plan badge: "CrickTeams Free" -> "Free".
   *
   * This was `| slice:11`, a POSITIONAL cut that assumed every plan's display name begins with the
   * literal "CrickTeams " (11 characters). It worked only because every shipped plan happens to.
   * Anything else is silently truncated mid-word — "QA Media Free" rendered as "ee", "QA Media Pro"
   * as "o" — and nothing validates the prefix, so a new tier, a localised name or a typo in the
   * admin plan editor produces a fragment with no clue where it came from. Strip the prefix only
   * when it is actually there, and otherwise show the name as-is.
   */
  get planBadgeLabel(): string {
    const name = this.clubSubscription?.planDisplayName ?? '';
    const prefix = 'CrickTeams ';
    return name.startsWith(prefix) ? name.slice(prefix.length) : name;
  }
  public userBaseInfo!: UserBaseInfo;
  public tournament: Tournament[] = [];
  public clubId!: string;
  inviteLink!: string;
  clubAllDatas: any;
  currentclubId: any;
  pendingPlayers: any[]=[]
  isManager= false
  constructor(
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private modalService: BsModalService,
    private playerService: PlayerService,
    protected commonService: CommonService,
    private clubService: ClubService,
    private tournamentService: TournamentService,
    private analyticsService: AnalyticsService,
    private alertService:AlertService,
    private cricteamsChatService: CricteamsChatService
  ) {
    this.setUserInfo();
    this.getProfileURI();

    // Load tournaments reactively when the active club ID becomes available
    // (fixes double-slash URL on first login when localStorage is not yet set)
    this.userService.currentClubId$
      .pipe(filter(id => !!id), distinctUntilChanged())
      .subscribe(() => {
        this.getTournamentPlayer();
        this.getClubs(); // Refresh subscription tier display
      });
  }

  ngOnInit(): void {
    this.commonService.isManager().then((isManager) => {
      this.isManager = isManager;
    });
    const clubId = localStorage.getItem('currentClubId');
    const userBaseInfo = localStorage.getItem('userBaseInfo');
    const clubAllData = userBaseInfo ? JSON.parse(userBaseInfo) : {};
    const inviteCode = clubAllData.clubs.find(
      (club: { id: string; inviteCode: string }) => club.id === clubId
    )?.inviteCode;
    this.clubAllDatas = clubAllData;
    this.inviteLink = inviteCode;
    this.currentclubId = localStorage.getItem('currentClubId');
    this.clubService
    .getPendingPlayers(this.currentclubId)
    .then((res) => this.pendingPlayers = res?.data || []);
    this.userService.isUserManagerOrCaptain().then((isManagerOrCaptain) => {
      this.isManagerOrCaptain = isManagerOrCaptain;
    });
    this.getClubs();
    if(!this.playerDetails){
      this.playerService.getPlayer().then((res: PlayerInfo | null) => {
        this.playerDetails = res;
        this.playerService.loggedinPlayer.next(this.playerDetails);
      });
    }
  }

  private async setUserInfo() {
    const user = await this.userService.getUserInfoFromLocalStorage();
    if (user) {
      const storedClubId = localStorage.getItem('lastLoggedInClubId');
      const activeClubs = user.clubs.filter((c: Club) => c.isActiveMember);
      const clubId = (storedClubId && activeClubs.some((c: Club) => c.id === storedClubId))
                     ? storedClubId
                     : activeClubs[0]?.id ?? user.clubs[0]?.id;
      localStorage.setItem('currentClubId', clubId);
      localStorage.setItem('lastLoggedInClubId', clubId);
      this.userService.currentClubId$.next(clubId);
      this.displayClubDropdown = user.clubs.filter((c: Club) => c.isActiveMember).length > 1;
      this.fullName = user.user.fullName;
    }
  }

  emitToggleClassEvent() {
    this.toggleClass.emit();
  }

  isNotification = false;

  notificationManu() {
    this.isNotification = !this.isNotification;
  }

  isProfileDropDownOpen = false;
  userDropdown() {
    this.isProfileDropDownOpen = !this.isProfileDropDownOpen;
  }

  isMyAccount = false;
  myAccountDropdown(template: TemplateRef<HTMLElement>) {
    this.modalRef = this.modalService.show(template);
    this.commonService.isLoaderBgTransparent.next(true);
  }

  isNotificationDropdown = false;
  NotificationDropdown() {
    this.isNotificationDropdown = !this.isNotificationDropdown;
  }

  logout() {
    this.analyticsService.trackEvent(analyticsEvents.LOG_OUT, {});
    this.cricteamsChatService.disconnect();
    this.authService.logout();
    localStorage.removeItem('access_token');
    clearAuthStorage();
    this.router.navigateByUrl('home');
  }

  toggleClubDropdown() {
    this.isClubDropdownOpen = !this.isClubDropdownOpen;
  }

  toggleAddDropdown() {
    this.isAddDropdownOpen = !this.isAddDropdownOpen;
  }

  private async getClubs() {
    const clubId = localStorage.getItem('currentClubId');
    const userInfo = localStorage.getItem('userBaseInfo');
    if (userInfo) {
      const clubData = JSON.parse(userInfo);
      this.clubsList = clubData.clubs.filter((club: Club) => club.isActiveMember && club.id !== clubId);
      const selectedClub = clubData.clubs.find((club: Club) => club.id === clubId);
      this.clubName = selectedClub?.name ?? '';
      this.clubImgUri = selectedClub?.sasUri ?? '';
      this.clubSubscription = selectedClub?.subscription ?? null;
    }
  }

  onClubSelect(selectedClub: Club) {
    this.activeClub = selectedClub;
    localStorage.setItem('currentClubId', this.activeClub.id);
    localStorage.setItem('lastLoggedInClubId', this.activeClub.id);
    this.userService.currentClubId$.next(this.activeClub.id);
    this.clubName = this.activeClub.name;
    this.clubImgUri = this.activeClub.sasUri ?? '';
    this.clubSubscription = selectedClub.subscription ?? null;
    this.clubService.isClubSwitch.next(true);
    this.getClubs();
    this.toggleClubDropdown();
    this.userService.isUserManagerOrCaptain().then((isManagerOrCaptain: boolean) => {
        this.isManagerOrCaptain = isManagerOrCaptain;
      });
    this.commonService.isManager().then((isManager) => {
      this.isManager = isManager;
    });
    window.location.reload();
  }


  handleClose(event: boolean) {
    if (event) {
      this.modalRef.hide();
    }
  }

  public openModal(template: TemplateRef<HTMLElement>){
    this.modalRef = this.modalService.show(template);
  }

  async getProfileURI() {
    this.userService.profileURIChanged.subscribe(async (isChanged) => {
      if (isChanged) {
        this.playerDetails = await this.playerService.getPlayer();
        this.fullName = `${this.playerDetails?.user.firstName ?? ''} ${this.playerDetails?.user.lastName ?? ''}`.trim();
      }
    });
    this.clubService.isClubUpdated.subscribe((isUpdated)=>{
      if(isUpdated){
        this.getClubs();
      }
    });
  }

  onUpgradeClick() {
    this.isClubDropdownOpen = false;
    this.upgradeClick.emit();
  }

  getTournamentPlayer() {
    this.userBaseInfo = JSON.parse(
      localStorage.getItem('userBaseInfo') || '{}'
    );
    this.clubId = localStorage.getItem('currentClubId') ?? '';
    this.tournamentService
      .getTournamentsByUserId(this.clubId, this.userBaseInfo.user.id)
      .then((res) => {
        if (res) {
          this.tournament = res;
          this.tournamentService.isShowTournamentButton.next(
            this.tournament.length > 0
          );
        }
      });
  }

}
